// components/AdvancedReports/reports/InvoiceSummary.js
import React, { useState, useMemo, useImperativeHandle, forwardRef } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import ReportLayout from '../core/ReportLayout';
import { formatCurrency, formatPercentage } from '../shared/utils';
import { exportToPDF, exportToExcel, exportToCSV, prepareEmailData } from '../shared/exportUtils';
import { FileText } from 'lucide-react';
import { modals } from '@mantine/modals';
import { successToast, errorToast } from '../../../lib/helpers';

const InvoiceSummary = forwardRef(function InvoiceSummary({ data, isLoading, searchTerm, filters: externalFilters = {}, hideHeader = false }, ref) {
    const [expandedDates, setExpandedDates] = useState(new Set());
    const [activeFilters, setActiveFilters] = useState({
        hasSearch: false,
        hasBranch: false
    });

    const groupedData = useMemo(() => {
        if (!data?.length) return [];

        // Filter data first based on searchTerm
        const filteredData = searchTerm
            ? data.filter(item =>
                item.inits?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.date?.includes(searchTerm) ||
                item.customer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.account?.toLowerCase().includes(searchTerm.toLowerCase())
            )
            : data;

        const grouped = Object.entries(filteredData.reduce((acc, curr) => {
            const date = curr.date;
            if (!acc[date]) {
                acc[date] = {
                    items: [],
                    totals: {
                        sales: 0,
                        cost: 0,
                        marginValue: 0,
                        invoices: 0,
                        credits: 0
                    }
                };
            }
            acc[date].items.push(curr);
            acc[date].totals.sales += curr.sales || 0;
            acc[date].totals.cost += curr.cost || 0;
            acc[date].totals.marginValue += curr.marginValue || 0;
            acc[date].totals.invoices += curr.invoices || 0;
            acc[date].totals.credits += curr.credits || 0;
            return acc;
        }, {}))
            .map(([date, data]) => ({
                date,
                ...data,
                totals: {
                    ...data.totals,
                    marginPercentage: data.totals.sales > 0 ? (data.totals.marginValue / data.totals.sales * 100) : 0
                }
            }))
            .sort((a, b) => new Date(b.date) - new Date(a.date));

        // Update active filters
        setActiveFilters({
            hasSearch: !!searchTerm,
            hasBranch: false // Can be enhanced if branch filter is passed
        });

        return grouped;
    }, [data, searchTerm]);

    // Calculate overall totals when filters are active
    const overallTotals = useMemo(() => {
        if (!activeFilters.hasSearch && !activeFilters.hasBranch) return null;
        
        return groupedData.reduce((acc, group) => ({
            sales: acc.sales + group.totals.sales,
            cost: acc.cost + group.totals.cost,
            marginValue: acc.marginValue + group.totals.marginValue,
            invoices: acc.invoices + group.totals.invoices,
            credits: acc.credits + group.totals.credits
        }), { sales: 0, cost: 0, marginValue: 0, invoices: 0, credits: 0 });
    }, [groupedData, activeFilters]);

    const toggleDate = (date) => {
        setExpandedDates(prev => {
            const newSet = new Set(prev);
            if (newSet.has(date)) {
                newSet.delete(date);
            } else {
                newSet.add(date);
            }
            return newSet;
        });
    };

    // Check if customer data is available in the first item
    const hasCustomerData = groupedData.length > 0 && groupedData[0]?.items?.[0] && (
        groupedData[0].items[0].customer || 
        groupedData[0].items[0].customerName || 
        groupedData[0].items[0].account ||
        groupedData[0].items[0].Account
    );

    // Prepare export data
    const prepareExportData = () => {
        const tableData = [];
        groupedData.forEach(group => {
            group.items.forEach(item => {
                tableData.push({
                    date: group.date,
                    customer: hasCustomerData ? (item.customer || item.customerName || item.name || 'N/A') : (item.inits || 'N/A'),
                    account: hasCustomerData ? (item.account || item.Account || item.inits || 'N/A') : (item.name || 'N/A'),
                    sales: item.sales || 0,
                    cost: item.cost || 0,
                    margin: item.marginValue || 0,
                    marginPercent: item.sales > 0 ? ((item.marginValue || 0) / item.sales * 100) : 0,
                    invoices: item.invoices || 0,
                    credits: item.credits || 0
                });
            });
        });

        return {
            tableData,
            columns: [
                { key: 'date', label: 'Date', format: 'date' },
                { key: 'customer', label: hasCustomerData ? 'Customer' : 'Initials', format: 'text' },
                { key: 'account', label: hasCustomerData ? 'Account' : 'Name', format: 'text' },
                { key: 'sales', label: 'Sales', format: 'currency' },
                { key: 'cost', label: 'Cost', format: 'currency' },
                { key: 'margin', label: 'Margin', format: 'currency' },
                { key: 'marginPercent', label: 'Margin %', format: 'percentage' },
                { key: 'invoices', label: 'Invoice Value', format: 'currency' },
                { key: 'credits', label: 'Credits', format: 'currency' }
            ]
        };
    };

    // Export handlers
    const handleExportPDF = () => {
        try {
            const exportData = prepareExportData();
            const filename = `invoice-summary-${new Date().toISOString().split('T')[0]}.pdf`;
            
            exportToPDF({
                title: 'Invoice Summary Report',
                subtitle: 'Daily sales breakdown by customer/rep',
                filename,
                columns: exportData.columns,
                tableData: exportData.tableData
            });
            
            successToast('PDF exported successfully');
        } catch (error) {
            console.error('PDF export failed:', error);
            errorToast('Failed to export PDF. Please try again.');
        }
    };

    const handleExportExcel = () => {
        try {
            const exportData = prepareExportData();
            const filename = `invoice-summary-${new Date().toISOString().split('T')[0]}.xlsx`;
            
            exportToExcel(exportData.tableData, {
                title: 'Invoice Summary Report',
                filename,
                columns: exportData.columns,
                tableData: exportData.tableData
            });
            
            successToast('Excel file exported successfully');
        } catch (error) {
            console.error('Excel export failed:', error);
            errorToast('Failed to export Excel file. Please try again.');
        }
    };

    const handleEmailReport = () => {
        modals.open({
            title: 'Email Invoice Summary Report',
            centered: true,
            children: (
                <EmailReportModal
                    onSend={async (email) => {
                        try {
                            const exportData = prepareExportData();
                            const emailData = prepareEmailData(exportData.tableData, {
                                title: 'Invoice Summary Report',
                                recipientEmail: email,
                                columns: exportData.columns,
                                tableData: exportData.tableData
                            });

                            // TODO: Implement email API call
                            console.log('Email data:', emailData);
                            successToast('Report email sent successfully');
                            modals.closeAll();
                        } catch (error) {
                            console.error('Email failed:', error);
                            errorToast('Failed to send email. Please try again.');
                        }
                    }}
                />
            )
        });
    };

    // Email Report Modal Component
    const EmailReportModal = ({ onSend }) => {
        const [email, setEmail] = useState('');
        const [loading, setLoading] = useState(false);

        return (
            <div className="p-4">
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Recipient Email
                    </label>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="recipient@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
                <div className="flex justify-end space-x-3">
                    <button
                        onClick={() => modals.closeAll()}
                        className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={async () => {
                            setLoading(true);
                            await onSend(email);
                            setLoading(false);
                        }}
                        disabled={!email || loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                        {loading ? 'Sending...' : 'Send Report'}
                    </button>
                </div>
            </div>
        );
    };

    // Expose export handlers via ref
    useImperativeHandle(ref, () => ({
        handleExportPDF,
        handleExportExcel,
        handleEmailReport
    }));

    if (isLoading) {
        if (hideHeader) {
            return (
                <div className="flex items-center justify-center py-12">
                    <div className="text-gray-500">Loading sales data...</div>
                </div>
            );
        }
        return (
            <ReportLayout
                title="Invoice Summary"
                subtitle="Daily sales breakdown"
                loading={isLoading}
                icon={FileText}
                iconColor="blue"
            >
                <div className="flex items-center justify-center py-12">
                    <div className="text-gray-500">Loading sales data...</div>
                </div>
            </ReportLayout>
        );
    }

    if (!groupedData || groupedData.length === 0) {
        if (hideHeader) {
            return (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                    <p className="text-gray-500">No sales data available for the selected period.</p>
                </div>
            );
        }
        return (
            <ReportLayout
                title="Invoice Summary"
                subtitle="Daily sales breakdown"
                icon={FileText}
                iconColor="blue"
                onExportPDF={handleExportPDF}
                onExportExcel={handleExportExcel}
                onEmailReport={handleEmailReport}
            >
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                    <p className="text-gray-500">No sales data available for the selected period.</p>
                </div>
            </ReportLayout>
        );
    }

    if (hideHeader) {
        return (
            <div className="space-y-4">
                {/* KPI Cards - Show when filters are active */}
                {overallTotals && (
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-blue-500">
                            <div className="text-sm font-medium text-gray-600 mb-1">Total Sales</div>
                            <div className="text-2xl font-bold text-gray-900">{formatCurrency(overallTotals.sales)}</div>
                            <div className="text-xs text-gray-500 mt-1">Filtered Total</div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-orange-500">
                            <div className="text-sm font-medium text-gray-600 mb-1">Total Cost</div>
                            <div className="text-2xl font-bold text-gray-900">{formatCurrency(overallTotals.cost)}</div>
                            <div className="text-xs text-gray-500 mt-1">Filtered Total</div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-green-500">
                            <div className="text-sm font-medium text-gray-600 mb-1">Total Margin</div>
                            <div className="text-2xl font-bold text-gray-900">{formatCurrency(overallTotals.marginValue)}</div>
                            <div className="text-xs text-gray-500 mt-1">
                                {overallTotals.sales > 0 ? formatPercentage((overallTotals.marginValue / overallTotals.sales) * 100) : '0%'} margin
                            </div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-purple-500">
                            <div className="text-sm font-medium text-gray-600 mb-1">Total Invoices</div>
                            <div className="text-2xl font-bold text-gray-900">{formatCurrency(overallTotals.invoices)}</div>
                            <div className="text-xs text-gray-500 mt-1">Sales before credits</div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-red-500">
                            <div className="text-sm font-medium text-gray-600 mb-1">Total Credits</div>
                            <div className="text-2xl font-bold text-gray-900">{formatCurrency(overallTotals.credits)}</div>
                            <div className="text-xs text-gray-500 mt-1">Filtered Total</div>
                        </div>
                    </div>
                )}

                {/* Daily Summary Cards - Collapsible */}
                {groupedData.map((group) => {
                    const isExpanded = expandedDates.has(group.date);
                    const dateFormatted = new Date(group.date).toLocaleDateString('en-GB', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    });

                    return (
                        <div key={group.date} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                            {/* Header - Clickable to expand/collapse */}
                            <div 
                                className="p-4 border-b bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                                onClick={() => toggleDate(group.date)}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        {isExpanded ? (
                                            <ChevronUp className="h-5 w-5 text-gray-500" />
                                        ) : (
                                            <ChevronDown className="h-5 w-5 text-gray-500" />
                                        )}
                                        <h3 className="text-lg font-semibold text-gray-900">{dateFormatted}</h3>
                                        <span className="text-sm text-gray-500 bg-gray-200 px-2 py-1 rounded">
                                            {group.items.length} {group.items.length === 1 ? 'transaction' : 'transactions'}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-4 text-sm">
                                        <div className="text-right">
                                            <div className="text-gray-500">Sales</div>
                                            <div className="font-semibold text-gray-900">{formatCurrency(group.totals.sales)}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-gray-500">Margin</div>
                                            <div className="font-semibold text-green-600">{formatCurrency(group.totals.marginValue)}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-gray-500">Margin %</div>
                                            <div className="font-semibold text-blue-600">{formatPercentage(group.totals.marginPercentage)}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Collapsible Content */}
                            {isExpanded && (
                                <div className="p-4">
                                    {/* Daily KPI Cards */}
                                    <div className="grid grid-cols-5 gap-4 mb-4">
                                        <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm border-l-4 border-l-blue-500">
                                            <div className="text-xs font-medium text-gray-600 mb-1">Total Sales</div>
                                            <div className="text-lg font-bold text-gray-900">{formatCurrency(group.totals.sales)}</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm border-l-4 border-l-orange-500">
                                            <div className="text-xs font-medium text-gray-600 mb-1">Total Cost</div>
                                            <div className="text-lg font-bold text-gray-900">{formatCurrency(group.totals.cost)}</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm border-l-4 border-l-green-500">
                                            <div className="text-xs font-medium text-gray-600 mb-1">Total Margin</div>
                                            <div className="text-lg font-bold text-gray-900">{formatCurrency(group.totals.marginValue)}</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm border-l-4 border-l-purple-500">
                                            <div className="text-xs font-medium text-gray-600 mb-1">Total Invoices</div>
                                            <div className="text-lg font-bold text-gray-900">{formatCurrency(group.totals.invoices)}</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm border-l-4 border-l-red-500">
                                            <div className="text-xs font-medium text-gray-600 mb-1">Total Credits</div>
                                            <div className="text-lg font-bold text-gray-900">{formatCurrency(group.totals.credits)}</div>
                                        </div>
                                    </div>

                                    {/* Transaction Table */}
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    {hasCustomerData ? (
                                                        <>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account</th>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Initials</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                                        </>
                                                    )}
                                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Sales</th>
                                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Margin</th>
                                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Margin %</th>
                                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice Value</th>
                                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Credits</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {group.items.map((item, index) => {
                                                    const customerName = item.customer || item.customerName || item.name || 'Unknown';
                                                    const account = item.account || item.Account || item.inits || 'N/A';
                                                    const marginPercent = item.sales > 0 ? ((item.marginValue || 0) / item.sales * 100) : 0;

                                                    return (
                                                        <tr key={`${item.date}-${item.inits}-${index}`} className="hover:bg-gray-50">
                                                            {hasCustomerData ? (
                                                                <>
                                                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                        {customerName}
                                                                    </td>
                                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                                        {account}
                                                                    </td>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                        {item.inits || 'N/A'}
                                                                    </td>
                                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                                        {item.name || 'N/A'}
                                                                    </td>
                                                                </>
                                                            )}
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 font-medium">
                                                                {formatCurrency(item.sales || 0)}
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">
                                                                {formatCurrency(item.cost || 0)}
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">
                                                                {formatCurrency(item.marginValue || 0)}
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                                    marginPercent >= 20 ? 'bg-green-100 text-green-800' :
                                                                    marginPercent >= 15 ? 'bg-yellow-100 text-yellow-800' :
                                                                    'bg-red-100 text-red-800'
                                                                }`}>
                                                                    {formatPercentage(marginPercent)}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">
                                                                {formatCurrency(item.invoices || 0)}
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">
                                                                {formatCurrency(item.credits || 0)}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    return (
        <ReportLayout
            title="Invoice Summary"
            subtitle="Daily sales breakdown by customer/rep"
            icon={FileText}
            iconColor="blue"
            onExportPDF={handleExportPDF}
            onExportExcel={handleExportExcel}
            onEmailReport={handleEmailReport}
        >
            <div className="space-y-4 px-6">
                {/* KPI Cards - Show when filters are active */}
                {overallTotals && (
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-blue-500">
                            <div className="text-sm font-medium text-gray-600 mb-1">Total Sales</div>
                            <div className="text-2xl font-bold text-gray-900">{formatCurrency(overallTotals.sales)}</div>
                            <div className="text-xs text-gray-500 mt-1">Filtered Total</div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-orange-500">
                            <div className="text-sm font-medium text-gray-600 mb-1">Total Cost</div>
                            <div className="text-2xl font-bold text-gray-900">{formatCurrency(overallTotals.cost)}</div>
                            <div className="text-xs text-gray-500 mt-1">Filtered Total</div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-green-500">
                            <div className="text-sm font-medium text-gray-600 mb-1">Total Margin</div>
                            <div className="text-2xl font-bold text-gray-900">{formatCurrency(overallTotals.marginValue)}</div>
                            <div className="text-xs text-gray-500 mt-1">
                                {overallTotals.sales > 0 ? formatPercentage((overallTotals.marginValue / overallTotals.sales) * 100) : '0%'} margin
                            </div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-purple-500">
                            <div className="text-sm font-medium text-gray-600 mb-1">Total Invoices</div>
                            <div className="text-2xl font-bold text-gray-900">{formatCurrency(overallTotals.invoices)}</div>
                            <div className="text-xs text-gray-500 mt-1">Sales before credits</div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-red-500">
                            <div className="text-sm font-medium text-gray-600 mb-1">Total Credits</div>
                            <div className="text-2xl font-bold text-gray-900">{formatCurrency(overallTotals.credits)}</div>
                            <div className="text-xs text-gray-500 mt-1">Filtered Total</div>
                        </div>
                    </div>
                )}

                {/* Daily Summary Cards - Collapsible */}
                {groupedData.map((group) => {
                    const isExpanded = expandedDates.has(group.date);
                    const dateFormatted = new Date(group.date).toLocaleDateString('en-GB', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    });

                    return (
                        <div key={group.date} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                            {/* Header - Clickable to expand/collapse */}
                            <div 
                                className="p-4 border-b bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                                onClick={() => toggleDate(group.date)}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        {isExpanded ? (
                                            <ChevronUp className="h-5 w-5 text-gray-500" />
                                        ) : (
                                            <ChevronDown className="h-5 w-5 text-gray-500" />
                                        )}
                                        <h3 className="text-lg font-semibold text-gray-900">{dateFormatted}</h3>
                                        <span className="text-sm text-gray-500 bg-gray-200 px-2 py-1 rounded">
                                            {group.items.length} {group.items.length === 1 ? 'transaction' : 'transactions'}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-4 text-sm">
                                        <div className="text-right">
                                            <div className="text-gray-500">Sales</div>
                                            <div className="font-semibold text-gray-900">{formatCurrency(group.totals.sales)}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-gray-500">Margin</div>
                                            <div className="font-semibold text-green-600">{formatCurrency(group.totals.marginValue)}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-gray-500">Margin %</div>
                                            <div className="font-semibold text-blue-600">{formatPercentage(group.totals.marginPercentage)}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Collapsible Content */}
                            {isExpanded && (
                                <div className="p-4">
                                    {/* Daily KPI Cards */}
                                    <div className="grid grid-cols-5 gap-4 mb-4">
                                        <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm border-l-4 border-l-blue-500">
                                            <div className="text-xs font-medium text-gray-600 mb-1">Total Sales</div>
                                            <div className="text-lg font-bold text-gray-900">{formatCurrency(group.totals.sales)}</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm border-l-4 border-l-orange-500">
                                            <div className="text-xs font-medium text-gray-600 mb-1">Total Cost</div>
                                            <div className="text-lg font-bold text-gray-900">{formatCurrency(group.totals.cost)}</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm border-l-4 border-l-green-500">
                                            <div className="text-xs font-medium text-gray-600 mb-1">Total Margin</div>
                                            <div className="text-lg font-bold text-gray-900">{formatCurrency(group.totals.marginValue)}</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm border-l-4 border-l-purple-500">
                                            <div className="text-xs font-medium text-gray-600 mb-1">Total Invoices</div>
                                            <div className="text-lg font-bold text-gray-900">{formatCurrency(group.totals.invoices)}</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm border-l-4 border-l-red-500">
                                            <div className="text-xs font-medium text-gray-600 mb-1">Total Credits</div>
                                            <div className="text-lg font-bold text-gray-900">{formatCurrency(group.totals.credits)}</div>
                                        </div>
                                    </div>

                                    {/* Transaction Table */}
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    {hasCustomerData ? (
                                                        <>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account</th>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Initials</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                                        </>
                                                    )}
                                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Sales</th>
                                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Margin</th>
                                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Margin %</th>
                                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice Value</th>
                                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Credits</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {group.items.map((item, index) => {
                                                    const customerName = item.customer || item.customerName || item.name || 'Unknown';
                                                    const account = item.account || item.Account || item.inits || 'N/A';
                                                    const marginPercent = item.sales > 0 ? ((item.marginValue || 0) / item.sales * 100) : 0;

                                                    return (
                                                        <tr key={`${item.date}-${item.inits}-${index}`} className="hover:bg-gray-50">
                                                            {hasCustomerData ? (
                                                                <>
                                                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                        {customerName}
                                                                    </td>
                                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                                        {account}
                                                                    </td>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                        {item.inits || 'N/A'}
                                                                    </td>
                                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                                        {item.name || 'N/A'}
                                                                    </td>
                                                                </>
                                                            )}
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 font-medium">
                                                                {formatCurrency(item.sales || 0)}
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">
                                                                {formatCurrency(item.cost || 0)}
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">
                                                                {formatCurrency(item.marginValue || 0)}
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                                    marginPercent >= 20 ? 'bg-green-100 text-green-800' :
                                                                    marginPercent >= 15 ? 'bg-yellow-100 text-yellow-800' :
                                                                    'bg-red-100 text-red-800'
                                                                }`}>
                                                                    {formatPercentage(marginPercent)}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">
                                                                {formatCurrency(item.invoices || 0)}
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">
                                                                {formatCurrency(item.credits || 0)}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </ReportLayout>
    );
});

export default InvoiceSummary;

